/**
 * Phase 27.53.P — Marketing Promo Campaign dialog.
 *
 * KR admin picks 1-6 shop items + writes a message ("Be ready for AEP —
 * order your materials now") and blasts it out to agents or agencies.
 * Reuses the marketing_templates catalogue for the picker. A "Send Test"
 * button lets the admin preview in their own inbox before broadcasting.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Send, Check, X, Megaphone, CalendarClock, Image as ImageIcon, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { marketingAPI } from '@/lib/api';
import RichTextEditor from '@/components/common/RichTextEditor';

const MAX_PRODUCTS = 6;

// Minimum future datetime (rounded up to the next 5 minutes) formatted
// as a `datetime-local` value so admins can't pick "now" or the past.
function minScheduleValue() {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function MarketingPromoDialog({ open, onOpenChange, userEmail }) {
  const [subject, setSubject] = useState('Be ready for AEP — Order your Marketing Materials');
  const [message, setMessage] = useState(
    '<p>AEP is around the corner. Get ahead of the rush and order your marketing materials early so you have everything ready when the season opens.</p><p>We just refreshed the catalogue — take a look at the <strong>featured pieces</strong> below and personalize them with your contact info in a couple clicks.</p>'
  );
  const [ctaLabel, setCtaLabel] = useState('Shop Now');
  const [ctaUrl, setCtaUrl] = useState('');
  const [audience, setAudience] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  // Phase 27.53.R — Hero image picker: 'none' (default gradient), 'upload'
  // (custom image), or 'product' (reuse a selected product's cover).
  const [heroMode, setHeroMode] = useState('none');
  const [heroImageId, setHeroImageId] = useState(null);
  const [heroPreviewUrl, setHeroPreviewUrl] = useState(null);
  const [heroTemplateId, setHeroTemplateId] = useState('');
  const [heroUploading, setHeroUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingList(true);
    marketingAPI.listTemplates({ limit: 100 })
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : (res.data.items || res.data.templates || []);
        setTemplates(items.filter((t) => t.active !== false));
      })
      .catch(() => toast.error('Could not load templates'))
      .finally(() => setLoadingList(false));
  }, [open]);

  const toggleTemplate = (tid) => {
    setSelectedIds((prev) => {
      if (prev.includes(tid)) return prev.filter((x) => x !== tid);
      if (prev.length >= MAX_PRODUCTS) {
        toast.warning(`Max ${MAX_PRODUCTS} products per promo`);
        return prev;
      }
      return [...prev, tid];
    });
  };

  const handleHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image exceeds 10 MB');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setHeroUploading(true);
    try {
      const res = await marketingAPI.uploadPromoHero(fd);
      setHeroImageId(res.data.image_id);
      setHeroPreviewUrl(URL.createObjectURL(file));
      toast.success('Hero image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setHeroUploading(false);
    }
  };

  const productsForHero = templates.filter((t) => selectedIds.includes(t.template_id));

  const submit = async ({ test }) => {
    if (!subject.trim()) return toast.error('Subject is required');
    // Rich text: strip tags to see if there's any actual content.
    const messagePlain = (message || '').replace(/<[^>]*>/g, '').trim();
    if (!messagePlain && selectedIds.length === 0) return toast.error('Add a message or at least one product');
    if (!test && scheduleEnabled && !scheduledAt) return toast.error('Pick a date & time for the schedule');
    setBusy(true);
    try {
      const payload = {
        subject: subject.trim(),
        message: (message || '').trim(),
        cta_label: (ctaLabel || '').trim() || 'Shop Now',
        cta_url: (ctaUrl || '').trim() || undefined,
        template_ids: selectedIds,
        audience,
      };
      if (test) {
        payload.test_email = userEmail;
      } else if (scheduleEnabled && scheduledAt) {
        // Convert the local datetime-local value → ISO for the server.
        payload.scheduled_at = new Date(scheduledAt).toISOString();
      }
      // Hero attachment.
      if (heroMode === 'upload' && heroImageId) {
        payload.hero_image_id = heroImageId;
      } else if (heroMode === 'product' && heroTemplateId) {
        payload.hero_from_template_id = heroTemplateId;
      }
      const res = await marketingAPI.sendPromo(payload);
      const d = res.data || {};
      if (test) {
        toast.success(`Test email sent to ${userEmail}`);
      } else if (d.mode === 'scheduled') {
        toast.success(`Promo scheduled for ${new Date(d.scheduled_at).toLocaleString()}`);
        onOpenChange(false);
      } else {
        toast.success(`Promo sent to ${d.sent} agent${d.sent === 1 ? '' : 's'}${d.failed_count ? ` (${d.failed_count} failed)` : ''}`);
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] p-0 gap-0 flex flex-col"
        data-testid="promo-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Megaphone size={18} className="text-purple-600" /> Send Marketing Promo
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Be ready for AEP — order your marketing materials"
                data-testid="promo-subject"
              />
            </div>
            <div className="space-y-2">
              <Label>Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger data-testid="promo-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents & Agencies</SelectItem>
                  <SelectItem value="agents">Individual Agents only</SelectItem>
                  <SelectItem value="agencies">Agency Principals & Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Message body</Label>
            <RichTextEditor
              value={message}
              onChange={setMessage}
              placeholder="Tell your agents why they should order now…"
              minHeight={170}
              testId="promo-message"
            />
            <p className="text-[11px] text-slate-500">
              Use the toolbar for <strong>Bold</strong>, <em>Italic</em>, <u>Underline</u> and bullet or numbered lists.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Button label <span className="text-slate-400 font-normal">· shown at the bottom of the email</span></Label>
            <Input
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              placeholder="Shop Now"
              maxLength={40}
              data-testid="promo-cta-label"
            />
            <p className="text-[11px] text-slate-500">
              Examples: "Shop Now", "Learn More", "Read Announcement", "Enroll Today", "View Details"
            </p>
            <div className="pt-1">
              <Label className="text-xs text-slate-600">Button link <span className="text-slate-400 font-normal">· optional — defaults to Marketing Center</span></Label>
              <Input
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                placeholder="/knowledge/aep-2027   or   https://example.com/landing"
                className="mt-1 font-mono text-xs"
                data-testid="promo-cta-url"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Leave empty to send agents to <code>/marketing</code>. Paste a full URL (external landing page) or a portal path (starts with <code>/</code>) like <code>/knowledge/aep-2027</code>.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Featured products <span className="text-slate-400 font-normal">· pick up to {MAX_PRODUCTS}</span></Label>
              <span className="text-xs text-slate-500" data-testid="promo-selected-count">
                {selectedIds.length} / {MAX_PRODUCTS} selected
              </span>
            </div>
            {loadingList ? (
              <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin mr-2" /> Loading catalogue…
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1" data-testid="promo-template-grid">
                {templates.length === 0 && (
                  <p className="col-span-full text-sm text-slate-400 italic py-4">No templates available.</p>
                )}
                {templates.map((t) => {
                  const on = selectedIds.includes(t.template_id);
                  return (
                    <button
                      type="button"
                      key={t.template_id}
                      onClick={() => toggleTemplate(t.template_id)}
                      className={`text-left rounded-lg border p-2 transition ${
                        on
                          ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-200'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`promo-template-${t.template_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-semibold text-slate-800 line-clamp-2">{t.title}</p>
                        {on ? (
                          <Check size={14} className="shrink-0 text-purple-600" />
                        ) : (
                          <span className="w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0" />
                        )}
                      </div>
                      <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                        {(t.category || '').replace(/_/g, ' ')}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-3 bg-white space-y-3">
            <div className="flex items-center gap-2">
              <ImageIcon size={14} className="text-slate-500" />
              <p className="text-sm font-medium text-slate-700">Hero image</p>
              <p className="text-[11px] text-slate-500">Shown as the big header of the email</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'none', label: 'Default gradient' },
                { id: 'product', label: 'Use product cover' },
                { id: 'upload', label: 'Upload custom image' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setHeroMode(opt.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    heroMode === opt.id
                      ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`hero-mode-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {heroMode === 'product' && (
              <div>
                {productsForHero.length === 0 ? (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                    Select at least one featured product above to use its cover as the hero.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {productsForHero.map((t) => {
                      const on = heroTemplateId === t.template_id;
                      return (
                        <button
                          key={t.template_id}
                          type="button"
                          onClick={() => setHeroTemplateId(on ? '' : t.template_id)}
                          className={`text-left rounded-md border p-2 text-xs transition ${
                            on
                              ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-200'
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                          data-testid={`hero-pick-${t.template_id}`}
                        >
                          <div className="flex items-center gap-1.5">
                            {on && <Check size={12} className="text-purple-600" />}
                            <span className="line-clamp-1">{t.title}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {heroMode === 'upload' && (
              <div className="flex items-center gap-3">
                <label
                  htmlFor="hero-file"
                  className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer"
                  data-testid="hero-upload-label"
                >
                  {heroUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {heroImageId ? 'Replace image' : 'Choose image (PNG / JPG / WEBP · max 10 MB)'}
                </label>
                <input
                  id="hero-file"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleHeroFile}
                  data-testid="hero-file-input"
                />
                {heroPreviewUrl && (
                  <img
                    src={heroPreviewUrl}
                    alt="Hero preview"
                    className="h-12 rounded border border-slate-200"
                    data-testid="hero-preview"
                  />
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/60">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="promo-schedule-toggle"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                className="w-4 h-4 accent-purple-600"
                data-testid="promo-schedule-toggle"
              />
              <label htmlFor="promo-schedule-toggle" className="text-sm font-medium text-slate-700 flex items-center gap-1.5 cursor-pointer">
                <CalendarClock size={14} /> Schedule for later
              </label>
            </div>
            {scheduleEnabled && (
              <div className="mt-3">
                <Label className="text-xs">Send date & time <span className="text-slate-400 font-normal">(your local time)</span></Label>
                <Input
                  type="datetime-local"
                  min={minScheduleValue()}
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="mt-1"
                  data-testid="promo-schedule-datetime"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Tip: For AEP kick-off, schedule this for Oct 1 at 8 AM ET.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-3 border-t border-slate-100 bg-white shrink-0 rounded-b-lg">
          <Button
            variant="outline"
            onClick={() => submit({ test: true })}
            disabled={busy}
            data-testid="promo-send-test-btn"
          >
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
            Send Test to Me
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              onClick={() => submit({ test: false })}
              disabled={busy}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="promo-send-btn"
            >
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : (
                scheduleEnabled ? <CalendarClock size={14} className="mr-2" /> : <Megaphone size={14} className="mr-2" />
              )}
              {scheduleEnabled ? 'Schedule Promo' : 'Broadcast Promo'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
